import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type CancelTokenSource, AxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { ComplianceApi, IOptInMethodTypes, IPlaneTypes } from 'entities/Compliance'
import { BillingApi } from 'entities/Billing'
import { openIntercom } from 'entities/Intercom'
import { DropdownCardsStore } from 'entities/Billing/ui/DropdownCards'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { PresentationModeStore } from 'widgets/PresentationMode'
import { LocalNumbersRoutes } from 'pages/settings/pages/compliance/pages/localNumbers/route/type'
import { type ComplianceLayerStore } from 'pages/settings/pages/compliance/store/complianceLayerStore'
import { UseCaseStore } from 'pages/settings/pages/compliance/pages/localNumbers/pages/CampaignUseCase/store/UseCaseStore'
import { MessagesStore } from 'pages/settings/pages/compliance/pages/localNumbers/pages/Messages/store/MessagesStore'
import { OptInMethodStore } from 'pages/settings/pages/compliance/pages/localNumbers/pages/OptInMethod/store/OptInMethodStore'
import { OptInContentStore } from 'pages/settings/pages/compliance/pages/localNumbers/pages/OptInContent/store/OptInContentStore'
import { ForbiddenMessageStore } from 'pages/settings/pages/compliance/pages/localNumbers/pages/ForbiddenMessageTopics/store/ForbiddenMessageStore'
import { PackageStore } from 'pages/settings/pages/compliance/pages/localNumbers/pages/Package/store/PackageStore'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { LocalNumbersNavigationStore } from 'pages/settings/pages/compliance/pages/localNumbers/store/localNumbersNavigationStore'
import { BusinessInfoStore } from 'pages/settings/pages/compliance/ui/CommonBusinessInfo/store/businessInfoStore'
import { GeneralInfoStore } from 'pages/settings/pages/compliance/ui/CommonGeneralInfo/store/generalInfoStore'
import { PeopleToContactStore } from 'pages/settings/pages/compliance/ui/CommonPeopleToContact/store/peopleToContactStore'

const titleMap: Record<string, string> = {
  [LocalNumbersRoutes.businessInfo]: 'Business info',
  [LocalNumbersRoutes.generalInfo]: 'General info',
  [LocalNumbersRoutes.peopleToContact]: 'People to contact',

  [LocalNumbersRoutes.campaignUseCases]: 'Campaign use case',
  [LocalNumbersRoutes.messages]: 'Messages',
  [LocalNumbersRoutes.optInMethod]: 'Opt-in method',
  [LocalNumbersRoutes.optInOnlineFormContent]: 'Opt-in consent: Online Form',
  [LocalNumbersRoutes.optInPaperFormContent]: 'Opt-in consent: Paper Form',
  [LocalNumbersRoutes.optInViaTextContent]: 'Opt-in consent: via Text',
  [LocalNumbersRoutes.optInQrCodeContent]: 'Opt-in consent: QR Code',
  [LocalNumbersRoutes.forbiddenMessageTopics]: 'Forbidden message topics',
  [LocalNumbersRoutes.package]: 'Package',
  [LocalNumbersRoutes.orderSummary]: 'Order summary',
}

const rejectedBrandUrls = [
  LocalNumbersRoutes.generalInfo,
  LocalNumbersRoutes.businessInfo,
  LocalNumbersRoutes.peopleToContact,
]

const startUrls = [
  LocalNumbersRoutes.campaignUseCases,
  LocalNumbersRoutes.messages,
  LocalNumbersRoutes.optInMethod,
]

export class LocalNumbersStepsStore {
  private readonly _localNumbersNavigationStore: LocalNumbersNavigationStore
  private readonly _useCaseStore: UseCaseStore
  private readonly _messagesStore: MessagesStore
  private readonly _optInMethodStore: OptInMethodStore
  private readonly _optInConsentStore: OptInContentStore
  private readonly _packageStore: PackageStore
  private readonly _forbiddenMessageStore: ForbiddenMessageStore
  private readonly _dropdownCardsStore: DropdownCardsStore
  private readonly _generalInfoStore: GeneralInfoStore
  private readonly _businessInfoStore: BusinessInfoStore
  private readonly _peopleToContactStore: PeopleToContactStore

  constructor(
    private _complianceLayerStore: ComplianceLayerStore,
    private _presentationStore: PresentationModeStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._generalInfoStore = new GeneralInfoStore(this._complianceLayerStore)
    this._businessInfoStore = new BusinessInfoStore()
    this._peopleToContactStore = new PeopleToContactStore(this._complianceLayerStore)
    this._localNumbersNavigationStore = new LocalNumbersNavigationStore(this._complianceLayerStore)
    this._useCaseStore = new UseCaseStore(this, this._entities)
    this._messagesStore = new MessagesStore(this)
    this._optInMethodStore = new OptInMethodStore(this)
    this._optInConsentStore = new OptInContentStore(this, this._presentationStore)
    this._packageStore = new PackageStore(this, this._entities)
    this._forbiddenMessageStore = new ForbiddenMessageStore(this, this._entities)
    this._dropdownCardsStore = new DropdownCardsStore(this._entities.billingStore)

    this.reactionInitCampaignData()
    this.initStores()
  }

  private _disposePageReaction: IReactionDisposer | null = null
  private _cancelPageSource: CancelTokenSource | null = null
  private _loading = false

  get packageStore() {
    return this._packageStore
  }

  get businessInfoStore() {
    return this._businessInfoStore
  }

  get dropdownCardsStore() {
    return this._dropdownCardsStore
  }

  get optInConsentStore() {
    return this._optInConsentStore
  }

  get messagesStore() {
    return this._messagesStore
  }

  get forbiddenMessageStore() {
    return this._forbiddenMessageStore
  }

  get optInMethodStore() {
    return this._optInMethodStore
  }

  get useCaseStore() {
    return this._useCaseStore
  }

  get localNumbersNavigationStore() {
    return this._localNumbersNavigationStore
  }

  get complianceLayerStore() {
    return this._complianceLayerStore
  }

  get errorStore() {
    return this._complianceLayerStore.errorStore
  }

  get peopleToContactStore() {
    return this._peopleToContactStore
  }

  get generalInfoStore() {
    return this._generalInfoStore
  }

  get aiSidebarStore() {
    return this._complianceLayerStore.aiSidebarStore
  }

  get isPrivateInternalRejected() {
    const { complianceServiceBrand, complianceServiceCampaign } = this._complianceLayerStore
    const internalRejected =
      complianceServiceBrand?.isInternalRejected || complianceServiceCampaign?.isInternalRejected

    return complianceServiceBrand?.isPrivateType && internalRejected
  }

  get isPrivateExternalRejected() {
    const { complianceServiceBrand, complianceServiceCampaign } = this._complianceLayerStore
    const isExternalRejected =
      complianceServiceBrand?.isExternalRejected || complianceServiceCampaign?.isExternalRejected

    return complianceServiceBrand?.isPrivateType && isExternalRejected
  }

  get isPublicInternalRejected() {
    const { complianceServiceBrand, complianceServiceCampaign } = this._complianceLayerStore
    const isInternalRejected =
      complianceServiceBrand?.isInternalRejected || complianceServiceCampaign?.isInternalRejected

    return complianceServiceBrand?.isPublicType && isInternalRejected
  }

  get isBrandExternalRejected() {
    const { complianceServiceBrand } = this._complianceLayerStore

    return complianceServiceBrand?.isExternalRejected
  }

  get noShowPackageStep() {
    return this.isPrivateInternalRejected || this.isPrivateExternalRejected
  }

  get noShowOrderSummaryStep() {
    return this.isPrivateInternalRejected || this.isPublicInternalRejected
  }

  get stepsStore() {
    return this._complianceLayerStore.stepsStore
  }

  get internalRejectedOnly() {
    const { complianceServiceCampaign, complianceServiceBrand } = this._complianceLayerStore

    const bothInternal =
      complianceServiceBrand?.isInternalRejected && complianceServiceCampaign?.isInternalRejected
    const innerBrandRejectedOnly =
      complianceServiceBrand?.isInternalRejected && !complianceServiceCampaign?.registrationError
    const innerComplianceRejectedOnly =
      complianceServiceCampaign?.isInternalRejected && !complianceServiceBrand?.registrationError

    return !!(bothInternal || innerBrandRejectedOnly || innerComplianceRejectedOnly)
  }

  get isSubmitDisabled() {
    return !this._dropdownCardsStore.activeId || !!this._dropdownCardsStore.paymentError
  }

  get stepLoading() {
    return this.complianceLayerStore.stepsStore.stepLoading
  }

  get allPaths() {
    const optInPaths: LocalNumbersRoutes[] = this._optInMethodStore.optInMethods
      .map((method) => {
        switch (method) {
          case IOptInMethodTypes.onlineForm:
            return LocalNumbersRoutes.optInOnlineFormContent
          case IOptInMethodTypes.paperForm:
            return LocalNumbersRoutes.optInPaperFormContent
          case IOptInMethodTypes.text:
            return LocalNumbersRoutes.optInViaTextContent
          case IOptInMethodTypes.qrCode:
            return LocalNumbersRoutes.optInQrCodeContent
          default:
            return null
        }
      })
      .filter(Boolean) as LocalNumbersRoutes[]

    const partOfPaths = [...startUrls, ...optInPaths, LocalNumbersRoutes.forbiddenMessageTopics]

    if (this.isBrandExternalRejected) {
      partOfPaths.unshift(...rejectedBrandUrls)
    }

    if (!this.noShowPackageStep) {
      partOfPaths.push(LocalNumbersRoutes.package)
    }
    if (!this.noShowOrderSummaryStep) {
      partOfPaths.push(LocalNumbersRoutes.orderSummary)
    }

    return partOfPaths
  }

  get allStepsLength() {
    // We have 6 steps + opt in. In case optInMethodStore.optInMethods.length > 0, opt in is included to the count
    return this._optInMethodStore.optInMethods.length
      ? this.allPaths.length
      : this.allPaths.length + 1
  }

  get campaignId() {
    return this._complianceLayerStore.complianceServiceCampaign?.id
  }

  get paymentError() {
    return this._dropdownCardsStore.paymentError
  }

  get priceId() {
    const { complianceServiceBrand, complianceServiceCampaign } = this.complianceLayerStore
    if (complianceServiceCampaign?.isStatusFailed && !complianceServiceBrand?.isStatusFailed) {
      return 'dlc_use_case_registration_vetting_new'
    }

    return this._packageStore.activePlaneType === IPlaneTypes.STANDARD
      ? 'a2p_brand_registration_fee_standard'
      : 'a2p_brand_registration_fee_lvs'
  }

  get isSubmitLoading() {
    return this._loading
  }

  dispose = () => {
    this._disposePageReaction?.()
    this._cancelPageSource?.cancel()

    this.complianceLayerStore.errorStore.reset()
  }

  setStepLoading = (value: boolean) => {
    this.complianceLayerStore.stepsStore.setStepLoading(value)
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  decreaseStep = (path: LocalNumbersRoutes) => {
    const lastStep = this.getLastStep(path)

    if (path === LocalNumbersRoutes.orderSummary) {
      this._dropdownCardsStore.clearPaymentError()
    }
    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.localNumbers.root}/${lastStep}`,
    })
  }

  getStep = (path: LocalNumbersRoutes, direction: -1 | 1): LocalNumbersRoutes | '' => {
    const allPaths = this.allPaths
    const currentIndex = allPaths.indexOf(path)
    const nextIndex = currentIndex + direction

    if (currentIndex !== -1 && nextIndex >= 0 && nextIndex < allPaths.length) {
      return allPaths[nextIndex]
    }

    return path
  }

  getLastStep = (path: LocalNumbersRoutes): LocalNumbersRoutes | '' => {
    return this.getStep(path, -1)
  }

  getNextStep = (path: LocalNumbersRoutes): LocalNumbersRoutes | '' => {
    return this.getStep(path, 1)
  }

  getSettingsStep = (path: LocalNumbersRoutes) => {
    const allPaths = this.allPaths

    return allPaths.indexOf(path) + 1
  }

  closeSetting = () => {
    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.localNumbers.root}`,
    })
  }

  learnMoreButtonAction = () => {
    openIntercom()
  }

  onSubmit = async (isCheckWithAI?: boolean) => {
    if (
      !this._packageStore.activePlaneType ||
      !this._dropdownCardsStore.activeId ||
      !this.campaignId
    )
      return

    try {
      this.stepsStore.setStepLoading(true)
      this.aiSidebarStore.setIsLoading(true)

      if (this.aiSidebarStore.isAiCheckrWillUse && isCheckWithAI) {
        const isShouldRedirect = await this.aiSidebarStore.analyzeAI10DLC()
        if (!isShouldRedirect) return
      }

      this._loading = true

      const { data } = await BillingApi.billingDepositReplenish({
        payment_method: this._dropdownCardsStore.activeId,
        price_id: this.priceId,
        type: 'price',
      })
      if (data) {
        const response = await ComplianceApi.submitLocalNumberApplication({
          ten_dlc_plan: this._packageStore.activePlaneType,
          ten_dlc_registration_submitted: true,
        })
        const { data } = await ComplianceApi.getComplianceServicesList()
        const campaign = data.campaigns.find((campaign) => campaign.id === this.campaignId)

        campaign && this.complianceLayerStore.set10DLCServiceCampaign(campaign)

        this._entities.complianceStore.fetchComplianceServicesList()
        response &&
          uiStore.changeRoute({
            path: `/${SettingsPath.compliance.localNumbers.root}`,
          })
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.errors.getError('payment_method')) {
          this._dropdownCardsStore.setPaymentError('Please select a different card')
        }
      }
      logger.error(e)
    } finally {
      this.stepsStore.setStepLoading(false)
      this.aiSidebarStore.setIsLoading(false)
      this._loading = false
    }
  }

  initStores = () => {
    const { complianceServiceCampaign, complianceServiceBrand, aiSidebarStore } =
      this._complianceLayerStore

    if (complianceServiceCampaign?.data) {
      this._useCaseStore.init(complianceServiceCampaign.data.useCaseData)
      this._messagesStore.init(complianceServiceCampaign.data.sampleMessagesData)
      this._optInMethodStore.init(complianceServiceCampaign.data.optInData)
      this._optInConsentStore.init(complianceServiceCampaign.data.optInData)
    }

    if (complianceServiceBrand) {
      this._businessInfoStore.initFromBrand(complianceServiceBrand.data)
      this._packageStore.init(complianceServiceBrand)
    }

    if (this._entities.featureFlagsStore.salesmsg_compliance_ai_v2) {
      aiSidebarStore.setIsAiCheckrWillUse(true)
    }
  }

  generalInfoNextStepClick = async () => {
    try {
      this.setStepLoading(true)

      // add request with a new EP

      uiStore.changeRoute({
        path: `/${SettingsPath.compliance.localNumbers.root}/${LocalNumbersRoutes.businessInfo}`,
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this.errorStore.prepareFieldsErrorAndSave(e.errors)
      }

      console.error(e)
    } finally {
      this.setStepLoading(false)
    }
  }

  businessInfoNextStepClick = async () => {
    try {
      this.setStepLoading(true)

      // add request with a new EP

      uiStore.changeRoute({
        path: `/${SettingsPath.compliance.localNumbers.root}/${LocalNumbersRoutes.peopleToContact}`,
      })
    } catch (e) {
      console.error(e)
    } finally {
      this.setStepLoading(false)
    }
  }

  nextStepClick = async () => {
    if (!this.peopleToContactStore.primaryContact.jobPosition) return

    const pointsOfContacts = [this.peopleToContactStore.firstContactToBackend]

    if (this.peopleToContactStore.isSecondContact) {
      pointsOfContacts.push(this.peopleToContactStore.secondContactToBackend)
    }

    try {
      this.complianceLayerStore?.stepsStore.setStepLoading(true)

      // add request with a new EP

      uiStore.changeRoute({
        path: `/${SettingsPath.compliance.localNumbers.root}/${LocalNumbersRoutes.campaignUseCases}`,
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this.errorStore?.prepareFieldsErrorAndSave(e.errors)
      }

      console.error(e)
    } finally {
      this.complianceLayerStore?.stepsStore?.setStepLoading(false)
    }
  }

  reactionInitCampaignData = () => {
    this._disposePageReaction?.()
    this._disposePageReaction = reaction(
      () => this._complianceLayerStore.complianceServiceCampaign,
      (complianceServiceCampaign) => {
        if (complianceServiceCampaign) {
          this.initStores()
        }
      }
    )
  }
}
